import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Compose-new email card. Used on both /drafts (via the New draft button) and
 * /inbox (via the New button in the header). Submits via api.createDraft with
 * kind='new'; on success the parent picks up the draft id to open the full
 * editor or navigate.
 */
export function NewDraftComposer({
  onCancel,
  onCreated,
  compact = false,
}: {
  onCancel: () => void;
  onCreated: (id: string) => void;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const accountsQuery = useQuery({
    queryKey: qk.accounts,
    queryFn: () => api.listAccounts(),
  });
  const accounts = accountsQuery.data?.accounts ?? [];
  const [accountId, setAccountId] = useState<string>('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!accountId && accounts.length) setAccountId(accounts[0]!.id);
  }, [accounts, accountId]);

  const create = useMutation({
    mutationFn: () =>
      api.createDraft({
        kind: 'new',
        accountId,
        to: parseEmails(to),
        cc: parseEmails(cc),
        bcc: parseEmails(bcc),
        subject,
        body,
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
      toast.success('Draft created');
      onCreated(res.draft.id);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Could not create draft';
      toast.error(msg);
    },
  });

  const inner = (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium">New draft</p>
          <p className="text-xs text-muted-foreground">
            Compose a fresh email from any connected account.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[120px_1fr]">
        <Label className="sm:pt-2">From</Label>
        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger>
            <SelectValue placeholder="Pick an account" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Label className="sm:pt-2">To</Label>
        <Input
          placeholder="comma-separated"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />

        <Label className="sm:pt-2">Cc</Label>
        <Input
          placeholder="optional"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
        />

        <Label className="sm:pt-2">Bcc</Label>
        <Input
          placeholder="optional"
          value={bcc}
          onChange={(e) => setBcc(e.target.value)}
        />

        <Label className="sm:pt-2">Subject</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>

      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className="min-h-[240px] text-[15px] leading-[1.6]"
        placeholder="Write here..."
      />

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => create.mutate()}
          disabled={
            create.isPending ||
            !accountId ||
            parseEmails(to).length === 0 ||
            (subject.trim().length === 0 && body.trim().length === 0)
          }
        >
          {create.isPending ? 'Creating...' : 'Create draft'}
        </Button>
      </div>
    </div>
  );

  if (compact) return inner;
  return (
    <Card>
      <CardContent className="p-5">{inner}</CardContent>
    </Card>
  );
}

function parseEmails(value: string): string[] {
  return value
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}
